import React, { useState, useEffect, useRef } from "react";
import VerifiedNavigation from "../../navigation/components/VerifiedNavigation";
import { InventoryServices } from "../services/InventoryServices";
import VariantModal from "./VariantModal";
import { useNavigate } from "react-router-dom";

const AddProducts = () => {
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [savePending, setSavePending] = useState(false);
  const [thumbnail, setThumbnail] = useState(null);
  const [images, setImages] = useState([]);
  const [variantModalOpen, setVariantModalOpen] = useState(false);
  const [editingVariantIndex, setEditingVariantIndex] = useState(null);
  const [variantDraft, setVariantDraft] = useState({
    color: "black",
    size: "S",
    stock: 0,
    sku: "",
    priceOverride: "",
  });
  const navigate = useNavigate("");
  const isMounted = useRef(true);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "",
    price: 0,
    stockStatus: "available",
    images: [],
    discount: 0,
    tags: [],
    collections: [],
    variants: [],
  });

  const toggleTag = (tag) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter((t) => t !== tag)
        : [...prev.tags, tag],
    }));
  };

  const toggleCollection = (collection) => {
    setFormData((prev) => ({
      ...prev,
      collections: prev.collections.includes(collection)
        ? prev.collections.filter((t) => t !== collection)
        : [...prev.collections, collection],
    }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);

    if (images.length + files.length > 6) {
      setMessage("You can only upload up to 6 images.");
      return;
    }

    const newFiles = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));

    setImages((prev) => [...prev, ...newFiles]);

    setFormData((prev) => ({
      ...prev,
      images: [...prev.images, ...files],
    }));

    if (!thumbnail && newFiles.length > 0) {
      setThumbnail(newFiles[0].preview);
    }
  };

  const handleRemoveImage = (index) => {
    const newImages = [...images];
    const [removed] = newImages.splice(index, 1);

    URL.revokeObjectURL(removed.preview);

    setImages(newImages);
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));

    if (thumbnail === removed.preview) {
      setThumbnail(newImages[0]?.preview || null);
    }
  };

  const handleSetThumbnail = (previewUrl) => {
    setThumbnail(previewUrl);
  };

  const totalStock = formData.variants.reduce(
    (sum, variant) => sum + (Number(variant.stock) || 0),
    0,
  );

  const openAddVariantModal = () => {
    setEditingVariantIndex(null);
    setVariantDraft({
      color: "black",
      size: "S",
      stock: 0,
      sku: "",
      priceOverride: "",
    });
    setVariantModalOpen(true);
  };

  const openEditVariantModal = (index) => {
    setEditingVariantIndex(index);
    setVariantDraft({
      ...formData.variants[index],
      stock: formData.variants[index].stock ?? 0,
      sku: formData.variants[index].sku ?? "",
      priceOverride: formData.variants[index].priceOverride ?? "",
    });
    setVariantModalOpen(true);
  };

  const handleVariantSave = (nextVariant) => {
    setFormData((prev) => {
      const nextVariants = [...prev.variants];

      if (editingVariantIndex !== null) {
        nextVariants[editingVariantIndex] = nextVariant;
      } else {
        nextVariants.push(nextVariant);
      }

      return {
        ...prev,
        variants: nextVariants,
      };
    });

    setEditingVariantIndex(null);
    setVariantDraft({
      color: "black",
      size: "S",
      stock: 0,
      sku: "",
      priceOverride: "",
    });
  };

  const handleVariantDelete = (index) => {
    setFormData((prev) => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index),
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setLoading(true);
    setSavePending(true);

    if (!formData.name || !formData.price || !formData.category) {
      setMessage("Please fill all required fields.");
      setLoading(false);
      return;
    }

    if (formData.price <= 0) {
      setMessage("Price must be greater than 0.");
      setLoading(false);
      return;
    }

    if (formData.images.length === 0) {
      setMessage("Please upload at least one image.");
      setLoading(false);
      return;
    }

    if (formData.variants.length === 0) {
      setMessage("Please add at least one variant.");
      setLoading(false);
      return;
    }

    const sendWithTimeout = (promise, ms) =>
      new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
          reject(new Error("timeout"));
        }, ms);

        promise
          .then((res) => {
            clearTimeout(timer);
            resolve(res);
          })
          .catch((err) => {
            clearTimeout(timer);
            reject(err);
          });
      });

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      formDataToSend.append("price", formData.price);
      formDataToSend.append("description", formData.description);
      formDataToSend.append("stockStatus", formData.stockStatus);
      formDataToSend.append("category", formData.category);
      formDataToSend.append("discount", formData.discount);
      formDataToSend.append("tags", JSON.stringify(formData.tags));
      formDataToSend.append(
        "collections",
        JSON.stringify(formData.collections),
      );
      formDataToSend.append("variants", JSON.stringify(formData.variants));
      formData.images.forEach((image) => {
        formDataToSend.append("images", image);
      });
      // compute approximate payload size (bytes)
      let payloadBytes = 0;
      for (const pair of formDataToSend.entries()) {
        const [key, val] = pair;
        if (val instanceof File) {
          payloadBytes += val.size || 0;
        } else if (typeof val === "string") {
          payloadBytes += new Blob([val]).size;
        }
      }

      const start = performance.now();

      // send full product payload to server for debugging/telemetry (non-blocking)
      try {
        const imagesMeta = (formData.images || []).map((f) => ({
          name: f?.name || "",
          size: f?.size || 0,
          type: f?.type || "",
        }));

        const productPayload = {
          name: formData.name,
          description: formData.description,
          price: formData.price,
          stockStatus: formData.stockStatus,
          category: formData.category,
          discount: formData.discount,
          tags: formData.tags,
          collections: formData.collections,
          variants: formData.variants,
          images: imagesMeta,
          meta: {
            payloadBytes,
            tier: formData.category,
          },
        };

        // wait for logging request to complete before proceeding (non-fatal)
        try {
          await InventoryServices.logProductPayload(productPayload);
        } catch (err) {
          console.warn("logProductPayload failed", err);
        }
      } catch (err) {
        console.warn("failed building product payload", err);
      }

      // call service with 30s timeout
      let response;
      try {
        response = await sendWithTimeout(
          InventoryServices.addNewProduct(formDataToSend),
          30000,
        );
      } catch (err) {
        const duration = Math.round(performance.now() - start);
        console.warn("product-save:failed", {
          reason: err.message || err,
          duration,
          payloadBytes,
        });
        if (err.message === "timeout") {
          setMessage("Save timed out after 30s. Please try again.");
        } else {
          setMessage(
            "Unable to save product. Please check your connection and try again.",
          );
        }
        return;
      }

      const duration = Math.round(performance.now() - start);

      if (typeof response === "string") {
        console.error("product-save:server-error", { response, duration });
        setMessage(response);
        return;
      }

      if (!response || response.status !== 200) {
        const serverMsg =
          response?.data?.message || response?.statusText || "Unknown error";
        console.error("product-save:server-error", {
          status: response?.status,
          message: serverMsg,
          duration,
        });
        setMessage(`Error ${response?.status || ""}: ${serverMsg}`);
        return;
      }

      console.info("product-save:success", {
        status: response.status,
        duration,
        payloadBytes,
      });

      setMessage("Product added successfully!");
      setFormData({
        name: "",
        description: "",
        category: "",
        price: "",
        stockStatus: "available",
        images: [],
        discount: 0,
        tags: [],
        collections: [],
        variants: [],
      });
      setImages([]);
      setThumbnail(null);
      try {
        localStorage.removeItem("unsavedProductForm");
      } catch (e) {}
      navigate("/manager/inventory");
    } catch (error) {
      console.error("product-save:unexpected", error);
      setMessage("An unexpected error occurred. " + (error?.message || ""));
    } finally {
      setLoading(false);
      setSavePending(false);
    }
  };

  useEffect(() => {
    return () => {
      images.forEach((img) => URL.revokeObjectURL(img.preview));
    };
  }, [images]);

  // persist form state to localStorage (text fields only)
  useEffect(() => {
    try {
      const toPersist = {
        formData: {
          ...formData,
          images: [],
        },
        thumbnail,
      };
      localStorage.setItem("unsavedProductForm", JSON.stringify(toPersist));
    } catch (e) {
      // ignore storage errors
    }
  }, [formData, thumbnail]);

  // restore persisted form on mount
  useEffect(() => {
    const saved = localStorage.getItem("unsavedProductForm");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed?.formData) {
          setFormData((prev) => ({ ...prev, ...parsed.formData }));
        }
        if (parsed?.thumbnail) setThumbnail(parsed.thumbnail);
      } catch (e) {
        /* noop */
      }
    }

    const beforeUnload = (e) => {
      if (
        savePending ||
        loading ||
        (formData && Object.values(formData).some((v) => v && v.length > 0))
      ) {
        e.preventDefault();
        e.returnValue =
          "You have unsaved changes. Are you sure you want to leave?";
        return e.returnValue;
      }
      return undefined;
    };

    window.addEventListener("beforeunload", beforeUnload);

    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
      isMounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="add-product">
      <VerifiedNavigation />
      <div className="add-product-container">
        <h1>Add Product</h1>
        {message && <p className="status-msg">{message}</p>}

        <div className="add-product-flex">
          <div className="product-images">
            <h2>Product Images</h2>
            <p className="sub-text">Upload up to 6 images & set a thumbnail</p>

            <div className="thumbnail">
              {thumbnail ? (
                <img src={thumbnail} alt="thumbnail" />
              ) : (
                <span>No Thumbnail</span>
              )}
            </div>

            <div className="image-grid">
              {images.map((img, i) => (
                <div className="image-box" key={i}>
                  <img
                    src={img.preview}
                    alt={`product-${i}`}
                    onClick={() => handleSetThumbnail(img.preview)}
                    className={thumbnail === img.preview ? "active-thumb" : ""}
                  />
                  <button
                    type="button"
                    className="remove-btn"
                    onClick={() => handleRemoveImage(i)}
                  >
                    ✕
                  </button>
                </div>
              ))}

              {images.length < 6 && (
                <label className="upload-box">
                  <input
                    type="file"
                    multiple
                    onChange={handleImageUpload}
                    hidden
                  />
                  +
                </label>
              )}
            </div>
          </div>

          <div className="product-detail">
            <h2>Product Detail</h2>
            <p className="sub-text">Set your product's information</p>

            <form onSubmit={handleSubmit}>
              <div className="section">
                <label htmlFor="">Product Name</label>
                <input
                  required
                  type="text"
                  name="name"
                  placeholder="Product name *"
                  className="input"
                  value={formData.name}
                  onChange={handleChange}
                />
              </div>

              <div className="section">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                    marginBottom: 10,
                  }}
                >
                  <label style={{ margin: 0 }}>Variants</label>
                  <button
                    type="button"
                    onClick={openAddVariantModal}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #f97316",
                      background: "#fff7ed",
                      color: "#c2410c",
                      borderRadius: 8,
                      cursor: "pointer",
                    }}
                  >
                    Add Variant
                  </button>
                </div>

                <div
                  style={{
                    border: "1px solid #eee",
                    borderRadius: 8,
                    padding: 12,
                    background: "#fafafa",
                  }}
                >
                  {formData.variants.length === 0 ? (
                    <div style={{ color: "#666" }}>No variants added yet.</div>
                  ) : (
                    <div style={{ display: "grid", gap: 8 }}>
                      {formData.variants.map((variant, index) => (
                        <div
                          key={`${variant.color}-${variant.size}-${index}`}
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1.6fr 1fr 1fr auto auto",
                            gap: 10,
                            alignItems: "center",
                            padding: "8px 10px",
                            border: "1px solid #e5e7eb",
                            borderRadius: 8,
                            background: "#fff",
                          }}
                        >
                          <div>
                            <strong>{variant.color}</strong>
                          </div>
                          <div>{variant.size}</div>
                          <div>Stock: {variant.stock}</div>
                          <button
                            type="button"
                            onClick={() => openEditVariantModal(index)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleVariantDelete(index)}
                          >
                            Delete
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div
                  style={{
                    marginTop: 12,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                  }}
                >
                  <strong>Total stock: {totalStock}</strong>
                  {(formData.reviewFlag || formData.review_flag) &&
                    totalStock === 0 && (
                      <span
                        style={{
                          color: "#b91c1c",
                          fontWeight: 600,
                          fontSize: 13,
                        }}
                      >
                        This product needs stock counts entered before it will
                        show as available
                      </span>
                    )}
                </div>
              </div>

              <div className="section">
                <label>Describe this product</label>
                <textarea
                  className="textarea"
                  name="description"
                  placeholder="Enter a description..."
                  value={formData.description}
                  onChange={handleChange}
                />
              </div>

              <div className="section">
                <label> Select the category</label>
                <select
                  className="input"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Category</option>
                  <option value="tops">Tops</option>
                  <option value="bottoms">Bottoms</option>
                  <option value="sets">Sets</option>
                  <option value="footwear">Footwear</option>
                  <option value="tees">Tees</option>
                  <option value="jackets">Jackets</option>
                  <option value="dresses">Dresses</option>
                  <option value="coats">Coats</option>
                  <option value="blazers">Blazers</option>
                  <option value="swimwear">Swimwear</option>
                  <option value="activewear">Activewear</option>
                  <option value="sleepwear">Sleepwear</option>
                  <option value="jumpsuits">Jumpsuits</option>
                  <option value="rompers">Rompers</option>
                  <option value="accessories">Accessories</option>
                </select>
              </div>

              <div className="section">
                <label>Price</label>
                <div className="price-row">
                  <input
                    type="number"
                    name="price"
                    placeholder="$0.00"
                    className="input"
                    value={formData.price}
                    onChange={handleChange}
                    required
                  />

                  <label htmlFor="discount">Discount</label>
                  <input
                    type="number"
                    name="discount"
                    placeholder="Discount % (0 for none)"
                    className="input"
                    value={formData.discount}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        discount: e.target.value,
                      }))
                    }
                    min="0"
                    max="100"
                  />
                  <label htmlFor="tags">Tags</label>
                  <ul className="tags-container">
                    {[
                      "Accessories",
                      "Women",
                      "Men",
                      "Tops",
                      "Bottoms",
                      "Footwear",
                    ].map((tag) => (
                      <li
                        key={tag}
                        onClick={() => toggleTag(tag)}
                        className={`tag-item ${
                          formData.tags.includes(tag) ? "selected" : ""
                        }`}
                      >
                        {tag}
                      </li>
                    ))}
                  </ul>

                  <div className="section">
                    <label>Collections</label>
                    <ul className="tags-container">
                      {[
                        "New Arrivals",
                        "Best Sellers",
                        "Trending Now",
                        "On Sale",
                        "Leaving Soon",
                      ].map((collection) => (
                        <li
                          key={collection}
                          onClick={() => toggleCollection(collection)}
                          className={`tag-item ${
                            formData.collections.includes(collection)
                              ? "selected"
                              : ""
                          }`}
                        >
                          {collection}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              <select
                className="input"
                name="stockStatus"
                value={formData.stockStatus}
                onChange={handleChange}
              >
                <option value="available">Available</option>
                <option value="unavailable">Unavailable</option>
              </select>

              <button type="submit" className="submit-btn" disabled={loading}>
                {loading ? "Adding..." : "Add to Products"}
              </button>
            </form>
          </div>
        </div>
      </div>

      {variantModalOpen && (
        <VariantModal
          isOpen={variantModalOpen}
          onClose={() => {
            setVariantModalOpen(false);
            setEditingVariantIndex(null);
            setVariantDraft({
              color: "black",
              size: "S",
              stock: 0,
              sku: "",
              priceOverride: "",
            });
          }}
          onSave={(variant) => {
            setFormData((prev) => {
              const updatedVariants = [...prev.variants];
              if (editingVariantIndex !== null) {
                updatedVariants[editingVariantIndex] = variant;
              } else {
                updatedVariants.push(variant);
              }
              return { ...prev, variants: updatedVariants };
            });
            setVariantModalOpen(false);
            setEditingVariantIndex(null);
            setVariantDraft({
              color: "black",
              size: "S",
              stock: 0,
              sku: "",
              priceOverride: "",
            });
          }}
          existingVariants={formData.variants}
          editingIndex={editingVariantIndex}
          basePrice={formData.price}
          initialVariant={variantDraft}
        />
      )}
    </div>
  );
};

export default AddProducts;
